import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@config/firebase';
import { FileText, Download, Trash2 } from 'lucide-react';
import { Button } from '@common/Button';
import { logError, logInfo } from '@utils/logger';
import { useSurvey } from '@contexts/SurveyContext';
import { formatBytes } from '@utils/formatters';

export const FilesTab = () => {
  const { activeSurvey } = useSurvey();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFiles = async () => {
      if (!activeSurvey?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Query files collection with proper surveyId filter
        const filesRef = collection(db, 'files');
        const q = query(filesRef, where('surveyId', '==', activeSurvey.id));
        const snapshot = await getDocs(q);
        
        const loadedFiles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setFiles(loadedFiles);
        logInfo(`Loaded ${loadedFiles.length} files`);
      } catch (err) {
        logError('Failed to load files:', err);
        setError('Failed to load survey files');
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, [activeSurvey?.id]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading files...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  if (!files.length) {
    return (
      <div className="text-center py-8 text-gray-400">
        No files uploaded yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {files.map(file => (
        <div key={file.id} className="bg-[#2A2B2E] p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">{file.name}</h3>
                <div className="text-sm text-gray-400">
                  {formatBytes(file.size)} • Uploaded {new Date(file.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => window.open(file.url, '_blank')}
                className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded"
                title="Download file"
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => {
                  // Implement delete functionality
                }}
                className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded"
                title="Delete file"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};